import React, { useState, useEffect, useCallback } from "react";
import Sidebar from "./Sidebar";
import "./DeviceDetail.css";
import API_BASE_URL from '../config/apiConfig';
import aromaImg from '../assets/aroma.png';
import airImg from '../assets/air.png';
import humiImg from '../assets/humi.png';
import speakerImg from '../assets/speaker.png';
import lampImg from '../assets/smartlamp.png';
import spotifyImg from '../assets/spotify.png';
// Smart Socket sẽ dùng SVG fallback (không dùng file hình)
const socketImg = null;

const DeviceDetail = ({
  isVisible,
  onBack,
  onLogout,
  deviceName = "Speaker",
  roomName = "Living room",
  onNavClick,
}) => {
  const [isOn, setIsOn] = useState(false); // Mặc định tắt cho Smart Socket
  const [volume, setVolume] = useState(70);
  const [brightness, setBrightness] = useState(70);
  const [isPlaying, setIsPlaying] = useState(false);
  const [power, setPower] = useState(0.0); // Công suất cho Smart Socket
  const [isLoading, setIsLoading] = useState(false);
  const [isResetting, setIsResetting] = useState(false);
  const [schedules, setSchedules] = useState([]); // Danh sách schedules
  const [countdownSeconds, setCountdownSeconds] = useState(0); // Countdown timer (giây)
  const [countdownActive, setCountdownActive] = useState(false); // Đang chạy countdown
  const [countdownAction, setCountdownAction] = useState(null); // Action sẽ thực hiện: 'ON' hoặc 'OFF'
  const isLamp = deviceName === "Smart Lamp";
  const isSmartSocket = deviceName === "Smart Socket";
  const isSpeaker = deviceName === "Speaker";
  
  // Hàm lấy hình ảnh thiết bị
  const getDeviceImage = () => {
    if (deviceName.includes('Lamp')) return lampImg;
    if (deviceName.includes('Speaker')) return speakerImg;
    if (deviceName.includes('Aroma')) return aromaImg;
    if (deviceName.includes('Humidifier')) return humiImg;
    if (deviceName.includes('Air Conditioner')) return airImg;
    return null;
  };

  const scenes = [
    { id: 1, name: "Morning coffee", schedule: "Everyday | 08:15 AM - 09:00 AM", enabled: false },
    { id: 2, name: "Movie Night", schedule: "Mon, Fri | 08:00 PM - 10:00 PM", enabled: true },
    { id: 3, name: "32th Birthday Kristin", schedule: "July 30th | 07:00 PM - 10:00 PM", enabled: false },
  ];

  // Fetch schedules cho Smart Socket
  const fetchSchedules = useCallback(async () => {
    if (!isSmartSocket) return;
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/devices/schedules`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (response.ok) {
        const data = await response.json();
        // Backend trả về với field names: isActive, isExecuted (Java boolean fields)
        const activeSchedules = (data.schedules || []).filter(s => 
          (s.isActive !== false) && (s.isExecuted !== true)
        );
        setSchedules(activeSchedules);
        console.log('Schedules fetched:', activeSchedules.length, activeSchedules);
      } else {
        console.error('Failed to fetch schedules:', response.status);
      }
    } catch (error) {
      console.error('Error fetching schedules:', error);
    }
  }, [isSmartSocket]);

  // Fetch schedules khi component mount và khi có schedule mới
  useEffect(() => {
    if (!isVisible || !isSmartSocket) return;
    
    // Fetch ngay lần đầu
    fetchSchedules();
    
    // Refresh mỗi 5 giây để cập nhật danh sách
    const scheduleInterval = setInterval(fetchSchedules, 5000);
    
    return () => {
      clearInterval(scheduleInterval);
    };
  }, [isVisible, isSmartSocket, fetchSchedules]);

  // Listen to schedule created event from ChatBot
  useEffect(() => {
    if (!isVisible || !isSmartSocket) return;

    const handleScheduleCreated = () => {
      console.log('Schedule created event received, refreshing schedules...');
      // Đợi một chút để backend lưu xong
      setTimeout(() => {
        fetchSchedules();
      }, 500);
    };

    window.addEventListener('smartSocketScheduleCreated', handleScheduleCreated);

    return () => {
      window.removeEventListener('smartSocketScheduleCreated', handleScheduleCreated);
    };
  }, [isVisible, isSmartSocket, fetchSchedules]);

  // Fetch power và status cho Smart Socket
  useEffect(() => {
    if (!isVisible || !isSmartSocket) return;

    const fetchPower = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/devices/power`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });
        
        if (response.ok) {
          const data = await response.json();
          const powerValue = data.power || 0.0;
          setPower(powerValue);
          console.log('Power updated:', powerValue, 'W');
        } else {
          console.error('Failed to fetch power:', response.status, response.statusText);
        }
      } catch (error) {
        console.error('Error fetching power:', error);
        // Không hiển thị alert để tránh spam
      }
    };

    const fetchStatus = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/devices/status`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        });
        
        if (response.ok) {
          const data = await response.json();
          const newStatus = data.isOn || false;
          // Chỉ cập nhật nếu khác với trạng thái hiện tại và không đang loading (tránh loop)
          if (newStatus !== isOn && !isLoading) {
            console.log('Status synced from server:', newStatus ? 'ON' : 'OFF');
            setIsOn(newStatus);
          }
        }
      } catch (error) {
        console.error('Error fetching status:', error);
      }
    };

    // Fetch ngay lần đầu
    fetchPower();
    fetchStatus();

    // Cập nhật mỗi 1 giây
    const powerInterval = setInterval(fetchPower, 1000);
    const statusInterval = setInterval(fetchStatus, 1000); // Polling status mỗi 1 giây

    return () => {
      clearInterval(powerInterval);
      clearInterval(statusInterval);
    };
  }, [isVisible, isSmartSocket, isOn, isLoading]);

  // Listen to countdown event from ChatBot
  useEffect(() => {
    if (!isVisible || !isSmartSocket) return;

    const handleCountdownEvent = (event) => {
      const { action, seconds } = event.detail;
      console.log('Received countdown event:', action, seconds);
      setCountdownSeconds(seconds);
      setCountdownAction(action);
      setCountdownActive(true);
    };

    window.addEventListener('smartSocketCountdown', handleCountdownEvent);

    return () => {
      window.removeEventListener('smartSocketCountdown', handleCountdownEvent);
    };
  }, [isVisible, isSmartSocket]);

  // Countdown timer
  useEffect(() => {
    if (!countdownActive) {
      return;
    }

    if (countdownSeconds <= 0) {
      // Countdown đã hết, thực hiện action
      if (countdownAction) {
        handleToggleSocket(countdownAction === 'ON');
        setCountdownActive(false);
        setCountdownAction(null);
        setCountdownSeconds(0);
      }
      return;
    }

    const timer = setInterval(() => {
      setCountdownSeconds(prev => {
        if (prev <= 1) {
          // Khi về 0, sẽ trigger useEffect để thực hiện action
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [countdownActive, countdownSeconds, countdownAction]);

  // Toggle relay cho Smart Socket
  const handleToggleSocket = async (value) => {
    setIsLoading(true);
    try {
      const cmd = value ? "ON" : "OFF";
      console.log('Toggling socket to:', cmd);
      
      const response = await fetch(`${API_BASE_URL}/api/devices/led?state=${cmd}`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.text();
        console.log('Toggle response:', result);
        setIsOn(value);
        // Status sẽ được đồng bộ tự động qua polling, không cần set thủ công
        // Không hiển thị alert thành công để tránh làm phiền
      } else {
        const errorText = await response.text();
        throw new Error(`Server returned ${response.status}: ${errorText}`);
      }
    } catch (error) {
      console.error('Error toggling socket:', error);
      alert(`Lỗi khi ${value ? 'bật' : 'tắt'} socket: ${error.message}`);
      // Revert lại trạng thái nếu lỗi
      setIsOn(!value);
    } finally {
      setIsLoading(false);
    }
  };

  // Hàm xóa schedule
  const handleDeleteSchedule = async (scheduleId) => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/devices/schedules/${scheduleId}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json' },
      });

      if (response.ok) {
        // Refresh danh sách schedules
        fetchSchedules();
      }
    } catch (error) {
      console.error('Error deleting schedule:', error);
      alert('Lỗi khi xóa lịch hẹn giờ');
    }
  };

  // Hàm bắt đầu countdown
  const handleStartCountdown = (action, seconds) => {
    setCountdownSeconds(seconds);
    setCountdownAction(action);
    setCountdownActive(true);
  };

  // Hàm hủy countdown
  const handleCancelCountdown = () => {
    setCountdownActive(false);
    setCountdownSeconds(0);
    setCountdownAction(null);
  };

  // Reset device (chỉ reset, không xóa thiết bị)
  const handleReset = async () => {
    const confirm = window.confirm(
      "This will reset the device to factory settings.\n" +
      "The device will restart and enter provisioning mode.\n" +
      "You can reconnect it later.\n\n" +
      "Continue?"
    );

    if (!confirm) return;

    setIsResetting(true);
    try {
      // Gửi reset command qua MQTT hoặc HTTP
      const response = await fetch(`${API_BASE_URL}/api/devices/reset`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          deviceName: deviceName,
          roomName: roomName
        }),
      });

      if (response.ok) {
        alert('Reset command sent successfully! Device will restart in provisioning mode.');
      } else {
        throw new Error('Failed to send reset command');
      }
    } catch (error) {
      console.error('Error resetting device:', error);
      alert(`Error resetting device: ${error.message}`);
    } finally {
      setIsResetting(false);
    }
  };

  if (!isVisible) return null;

  return (
    <div className="dd-page">
      <Sidebar activeId="devices" onLogout={onLogout} onNavClick={onNavClick} />

      <div className="dd-content">
        {/* Header */}
        <div className="dd-header">
          <div className="dd-header-left">
            <button className="dd-back" onClick={onBack} aria-label="Back">
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="19" y1="12" x2="5" y2="12"></line>
                <polyline points="12 19 5 12 12 5"></polyline>
              </svg>
            </button>

            <div className="dd-breadcrumb">
              <span className="dd-room">{roomName}</span>
              <span className="dd-device">{deviceName}</span>
            </div>
          </div>

          {isSmartSocket && (
            <div className="dd-header-right">
              {isLoading ? (
                <div className="dd-loading-spinner" style={{ width: '20px', height: '20px', border: '2px solid #e5e7eb', borderTop: '2px solid #1a73e8', borderRadius: '50%', animation: 'spin 1s linear infinite' }}></div>
              ) : (
                <label className="dd-toggle">
                  <input
                    type="checkbox"
                    checked={isOn}
                    onChange={(e) => handleToggleSocket(e.target.checked)}
                  />
                  <span className="dd-slider" />
                </label>
              )}
            </div>
          )}

          <div className="dd-home">
            {/* avatar giả */}
            <span style={{ fontWeight: 700 }}>My Home</span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="6 9 12 15 18 9" />
            </svg>
          </div>
        </div>

        {/* Main */}
        <div className="dd-main">
          <div className="dd-body">
            {/* LEFT */}
            <div className="dd-left">
              {isSmartSocket ? (
                /* Smart Socket Card - Cải thiện UI */
                <div className="dd-socket-card">
                  {/* Status Badge */}
                  <div style={{
                    position: 'absolute',
                    top: '20px',
                    right: '20px',
                    padding: '8px 16px',
                    borderRadius: '20px',
                    background: isOn ? 'linear-gradient(135deg, #ff8c00 0%, #ff6b00 100%)' : '#e5e7eb',
                    color: isOn ? '#fff' : '#6b7280',
                    fontSize: '14px',
                    fontWeight: '600',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    boxShadow: isOn ? '0 4px 12px rgba(255, 140, 0, 0.3)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
                    transition: 'all 0.3s ease'
                  }}>
                    <div style={{
                      width: '8px',
                      height: '8px',
                      borderRadius: '50%',
                      background: isOn ? '#fff' : '#9ca3af',
                      animation: isOn ? 'pulse 2s infinite' : 'none'
                    }} />
                    {isOn ? 'ON' : 'OFF'}
                  </div>

                  {/* Socket Image/Icon */}
                  <div className="dd-socket-stage" style={{
                    padding: '40px',
                    background: isOn 
                      ? 'linear-gradient(135deg, #fff5e6 0%, #ffe6cc 100%)'
                      : 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
                    borderRadius: '50%',
                    boxShadow: isOn 
                      ? '0 0 40px rgba(255, 140, 0, 0.5), 0 0 20px rgba(255, 140, 0, 0.3), inset 0 0 20px rgba(255, 140, 0, 0.1)' 
                      : '0 0 30px rgba(0, 0, 0, 0.1), 0 0 10px rgba(0, 0, 0, 0.05)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '240px',
                    height: '240px',
                    margin: '0 auto 30px',
                    transition: 'all 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55)',
                    transform: isOn ? 'scale(1.05)' : 'scale(1)',
                    position: 'relative'
                  }}>
                    {socketImg ? (
                      <img 
                        src={socketImg} 
                        alt="Smart Socket" 
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain',
                          padding: '20px',
                          filter: isOn ? 'brightness(1.1) saturate(1.2)' : 'brightness(0.8) saturate(0.8)',
                          transition: 'filter 0.3s ease'
                        }}
                      />
                    ) : (
                      <svg width="140" height="140" viewBox="0 0 24 24" fill="none" stroke={isOn ? "#ff8c00" : "#9ca3af"} strokeWidth="1.8" style={{ transition: 'stroke 0.3s ease' }}>
                        <path d="M12 2L2 7l10 5 10-5-10-5z" />
                        <path d="M2 17l10 5 10-5" />
                        <path d="M2 12l10 5 10-5" />
                      </svg>
                    )}
                    {/* Glow effect khi bật */}
                    {isOn && (
                      <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '100%',
                        height: '100%',
                        borderRadius: '50%',
                        background: 'radial-gradient(circle, rgba(255, 140, 0, 0.3) 0%, transparent 70%)',
                        animation: 'pulse-glow 2s infinite',
                        pointerEvents: 'none'
                      }} />
                    )}
                  </div>

                  {/* Power Display - Cải thiện */}
                  <div style={{
                    marginTop: '20px',
                    padding: '20px 40px',
                    background: power > 0 
                      ? 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)'
                      : 'linear-gradient(135deg, #f9fafb 0%, #f3f4f6 100%)',
                    borderRadius: '20px',
                    border: `2px solid ${power > 0 ? '#86efac' : '#e5e7eb'}`,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '12px',
                    boxShadow: power > 0 ? '0 4px 12px rgba(16, 185, 129, 0.2)' : '0 2px 8px rgba(0, 0, 0, 0.05)',
                    transition: 'all 0.3s ease'
                  }}>
                    <div style={{
                      padding: '10px',
                      borderRadius: '12px',
                      background: power > 0 ? '#dcfce7' : '#f3f4f6',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke={power > 0 ? "#10b981" : "#9ca3af"} strokeWidth="2.5">
                        <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z" />
                      </svg>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                      <span style={{
                        fontSize: '12px',
                        color: power > 0 ? '#059669' : '#6b7280',
                        fontWeight: '500',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                      }}>
                        Power Consumption
                      </span>
                      <span style={{
                        fontSize: '36px',
                        fontWeight: 'bold',
                        color: power > 0 ? '#047857' : '#6b7280',
                        lineHeight: '1',
                        marginTop: '4px'
                      }}>
                        {power.toFixed(2)} <span style={{ fontSize: '20px', fontWeight: '500' }}>W</span>
                      </span>
                    </div>
                  </div>

                  {/* Large Toggle Switch - Cải thiện */}
                  <div style={{ 
                    marginTop: '50px', 
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '16px'
                  }}>
                    <span style={{
                      fontSize: '14px',
                      color: '#6b7280',
                      fontWeight: '500',
                      textTransform: 'uppercase',
                      letterSpacing: '1px'
                    }}>
                      {isOn ? 'Device is ON' : 'Device is OFF'}
                    </span>
                    <label className="dd-toggle" style={{ 
                      transform: 'scale(2.2)',
                      cursor: isLoading ? 'not-allowed' : 'pointer'
                    }}>
                      <input
                        type="checkbox"
                        checked={isOn}
                        onChange={(e) => handleToggleSocket(e.target.checked)}
                        disabled={isLoading}
                      />
                      <span className="dd-slider" style={{
                        backgroundColor: isOn ? '#ff8c00' : '#d1d5db',
                        boxShadow: isOn ? '0 4px 12px rgba(255, 140, 0, 0.4)' : '0 2px 8px rgba(0, 0, 0, 0.1)',
                        transition: 'all 0.3s cubic-bezier(0.68, -0.55, 0.265, 1.55)'
                      }} />
                    </label>
                    {isLoading && (
                      <div style={{
                        marginTop: '10px',
                        fontSize: '12px',
                        color: '#6b7280',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <div className="dd-loading-spinner" style={{ width: '16px', height: '16px' }}></div>
                        Processing...
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div className={isLamp ? "dd-lamp-card" : "dd-speaker-card"}>
                  <div className={isLamp ? "dd-lamp-stage" : "dd-speaker-stage"}>
                    <div className="dd-ring r1" />
                    <div className="dd-ring r2" />
                    <div className="dd-ring r3" />

                    <div className={isLamp ? "dd-lamp" : "dd-speaker"}>
                      <div className={isLamp ? "dd-lamp-icon" : "dd-speaker-icon"}>
                        {getDeviceImage() ? (
                          /* Hiển thị hình từ assets */
                          <img 
                            src={getDeviceImage()} 
                            alt={deviceName}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain',
                              padding: '10px'
                            }}
                          />
                        ) : (
                          /* Fallback về SVG nếu không có hình */
                          isLamp ? (
                            /* icon đèn thông minh */
                            <svg width="110" height="110" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4">
                              <path d="M9 21h6" />
                              <path d="M12 2a7 7 0 0 0-7 7c0 2.5 1.5 4.5 3 6l1.5 2h5L15 15c1.5-1.5 3-3.5 3-6a7 7 0 0 0-7-7z" />
                              <path d="M9 8h6" />
                              <circle cx="12" cy="11" r="1.5" fill="currentColor" />
                            </svg>
                          ) : (
                            /* icon nhà + wifi */
                            <svg width="110" height="110" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4">
                              <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
                              <path d="M9 22V12h6v10" />
                            </svg>
                          )
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Controls - Ẩn cho Smart Socket vì đã có toggle lớn ở trên */}
              {!isSmartSocket && (
              <div className="dd-controls">
                <div className="dd-row">
                  <div className="dd-label">Status</div>
                  <div className="dd-rightcell">
                    <label className="dd-toggle">
                      <input
                        type="checkbox"
                        checked={isOn}
                        onChange={(e) => setIsOn(e.target.checked)}
                      />
                      <span className="dd-slider" />
                    </label>
                  </div>
                </div>

                <div className="dd-row">
                  <div className="dd-label">Battery</div>
                  <div className="dd-rightcell">
                    <span className="dd-battery">
                      <span className="dd-battery-dot" />
                      90%
                    </span>
                  </div>
                </div>

                {isLamp ? (
                  <div className="dd-row">
                    <div className="dd-label">Brightness</div>
                    <div className="dd-rightcell" style={{ justifyContent: "flex-start" }}>
                      <div className="dd-volumeWrap" style={{ ["--vol"]: `${brightness}%` }}>
                        <div className="dd-volbar">
                          <div className="dd-volfill" />
                          <div className="dd-voldots">
                            {Array.from({ length: 12 }).map((_, i) => <span key={i} />)}
                          </div>
                          <input
                            className="dd-volrange"
                            type="range"
                            min="0"
                            max="100"
                            value={brightness}
                            onChange={(e) => setBrightness(Number(e.target.value))}
                          />
                        </div>
                        <div className="dd-volpct">{brightness}%</div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="dd-row">
                    <div className="dd-label">Volume</div>
                    <div className="dd-rightcell" style={{ justifyContent: "flex-start" }}>
                      <div className="dd-volumeWrap" style={{ ["--vol"]: `${volume}%` }}>
                        <div className="dd-volbar">
                          <div className="dd-volfill" />
                          <div className="dd-voldots">
                            {Array.from({ length: 12 }).map((_, i) => <span key={i} />)}
                          </div>
                          <input
                            className="dd-volrange"
                            type="range"
                            min="0"
                            max="100"
                            value={volume}
                            onChange={(e) => setVolume(Number(e.target.value))}
                          />
                        </div>
                        <div className="dd-volpct">{volume}%</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              )}

              {/* Reset Button - Chỉ hiển thị cho Smart Socket */}
              {isSmartSocket && (
              <div className="dd-row" style={{ marginTop: '20px', paddingTop: '20px', borderTop: '1px solid #e5e7eb' }}>
                <div className="dd-label">Device Reset</div>
                <div className="dd-rightcell">
                  <button
                    onClick={handleReset}
                    disabled={isResetting}
                    style={{
                      backgroundColor: isResetting ? '#9ca3af' : '#ef4444',
                      color: 'white',
                      border: 'none',
                      padding: '8px 16px',
                      borderRadius: '8px',
                      cursor: isResetting ? 'not-allowed' : 'pointer',
                      fontSize: '14px',
                      fontWeight: '500',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}
                  >
                    {isResetting ? (
                      <>
                        <span style={{ 
                          display: 'inline-block',
                          width: '14px',
                          height: '14px',
                          border: '2px solid white',
                          borderTop: '2px solid transparent',
                          borderRadius: '50%',
                          animation: 'spin 1s linear infinite'
                        }}></span>
                        Resetting...
                      </>
                    ) : (
                      <>
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <path d="M3 12a9 9 0 0 1 9-9 9.75 9.75 0 0 1 6.74 2.74L21 8" />
                          <path d="M21 3v5h-5" />
                          <path d="M21 12a9 9 0 0 1-9 9 9.75 9.75 0 0 1-6.74-2.74L3 16" />
                          <path d="M3 21v-5h5" />
                        </svg>
                        Reset Device
                      </>
                    )}
                  </button>
                </div>
              </div>
              )}
            </div>

            {/* RIGHT */}
            <div className="dd-right">
              {isSpeaker && (
                /* Spotify - chỉ hiển thị cho Speaker */
                <div className="dd-card">
                  <div className="dd-cardhead">
                    <div className="dd-cardtitle">
                      <h3>Spotify</h3>
                      <p>{roomName}</p>
                    </div>
                    <button className="dd-chevron" aria-label="Expand">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </button>
                  </div>

                  <div className="dd-spotifyCover">
                    {/* Sử dụng hình Spotify từ assets */}
                    <img
                      alt="Spotify cover"
                      src={spotifyImg}
                    />
                    <div className="dd-coverOverlay" />
                  </div>

                  <div className="dd-progressRow">
                    <div className="dd-progress"><div /></div>
                    <div className="dd-time">2:45 min</div>
                  </div>

                  <div className="dd-songRow">
                    <div className="dd-songInfo">
                      <h4>As it was</h4>
                      <p>Harry Styles</p>
                    </div>

                    <div className="dd-playerBtns">
                      <button className="dd-iconBtn" aria-label="Prev">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polygon points="19 20 9 12 19 4 19 20" />
                          <line x1="5" y1="19" x2="5" y2="5" />
                        </svg>
                      </button>

                      <button className="dd-play" onClick={() => setIsPlaying(!isPlaying)} aria-label="Play">
                        {isPlaying ? (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                            <rect x="6" y="4" width="4" height="16" />
                            <rect x="14" y="4" width="4" height="16" />
                          </svg>
                        ) : (
                          <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                            <polygon points="5 3 19 12 5 21 5 3" />
                          </svg>
                        )}
                      </button>

                      <button className="dd-iconBtn" aria-label="Next">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polygon points="5 4 15 12 5 20 5 4" />
                          <line x1="19" y1="5" x2="19" y2="19" />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {isLamp && (
                /* Light Settings - chỉ hiển thị cho Smart Lamp */
                <div className="dd-card">
                  <div className="dd-cardhead">
                    <div className="dd-cardtitle">
                      <h3>Light Settings</h3>
                      <p>{roomName}</p>
                    </div>
                    <button className="dd-chevron" aria-label="Expand">
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="6 9 12 15 18 9" />
                      </svg>
                    </button>
                  </div>

                  <div className="dd-lampColorGrid">
                    <div className="dd-colorOption active">
                      <div className="dd-colorCircle" style={{ background: '#fbbf24' }} />
                      <span>Warm</span>
                    </div>
                    <div className="dd-colorOption">
                      <div className="dd-colorCircle" style={{ background: '#ffffff' }} />
                      <span>White</span>
                    </div>
                    <div className="dd-colorOption">
                      <div className="dd-colorCircle" style={{ background: '#60a5fa' }} />
                      <span>Cool</span>
                    </div>
                    <div className="dd-colorOption">
                      <div className="dd-colorCircle" style={{ background: '#a78bfa' }} />
                      <span>Purple</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Schedules & Countdown - Chỉ hiển thị cho Smart Socket */}
              {isSmartSocket && (
                <>
                  {/* Countdown Timer */}
                  <div className="dd-card" style={{ marginBottom: '20px' }}>
                    <div className="dd-cardhead">
                      <div className="dd-cardtitle">
                        <h3>Quick Timer</h3>
                        <p>Turn On/Off after X seconds</p>
                      </div>
                    </div>
                    <div style={{ padding: '20px' }}>
                      {countdownActive ? (
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#1a73e8', marginBottom: '10px' }}>
                            {Math.floor(countdownSeconds / 60)}:{(countdownSeconds % 60).toString().padStart(2, '0')}
                          </div>
                          <p style={{ marginBottom: '15px', color: '#666' }}>
                            Will {countdownAction === 'ON' ? 'turn on' : 'turn off'} after
                          </p>
                          <button
                            onClick={handleCancelCountdown}
                            style={{
                              padding: '10px 20px',
                              backgroundColor: '#ef4444',
                              color: 'white',
                              border: 'none',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              fontSize: '14px',
                              fontWeight: '500'
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div>
                          <p style={{ marginBottom: '15px', fontSize: '14px', color: '#666', fontWeight: '500', textAlign: 'center' }}>
                            Select time and action:
                          </p>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                            {[5, 30, 60, 300, 600, 1800].map((seconds) => (
                              <div key={seconds} style={{ display: 'flex', gap: '10px' }}>
                                <button
                                  onClick={() => handleStartCountdown('ON', seconds)}
                                  style={{
                                    flex: '1',
                                    padding: '12px',
                                    backgroundColor: '#dcfce7',
                                    border: '2px solid #10b981',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    color: '#047857',
                                    transition: 'all 0.2s',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = '#bbf7d0';
                                    e.target.style.transform = 'scale(1.02)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = '#dcfce7';
                                    e.target.style.transform = 'scale(1)';
                                  }}
                                >
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                    <circle cx="12" cy="12" r="10" />
                                    <polyline points="12 6 12 12 16 14" />
                                  </svg>
                                  Turn On after {seconds < 60 ? `${seconds}s` : seconds < 3600 ? `${seconds / 60} min` : `${seconds / 3600} hr`}
                                </button>
                                <button
                                  onClick={() => handleStartCountdown('OFF', seconds)}
                                  style={{
                                    flex: '1',
                                    padding: '12px',
                                    backgroundColor: '#fee2e2',
                                    border: '2px solid #ef4444',
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: '600',
                                    color: '#991b1b',
                                    transition: 'all 0.2s',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    gap: '8px'
                                  }}
                                  onMouseEnter={(e) => {
                                    e.target.style.backgroundColor = '#fecaca';
                                    e.target.style.transform = 'scale(1.02)';
                                  }}
                                  onMouseLeave={(e) => {
                                    e.target.style.backgroundColor = '#fee2e2';
                                    e.target.style.transform = 'scale(1)';
                                  }}
                                >
                                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                    <circle cx="12" cy="12" r="10" />
                                    <line x1="8" y1="12" x2="16" y2="12" />
                                  </svg>
                                  Turn Off after {seconds < 60 ? `${seconds}s` : seconds < 3600 ? `${seconds / 60} min` : `${seconds / 3600} hr`}
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Schedules List */}
                  <div className="dd-card">
                    <div className="dd-cardhead">
                      <div className="dd-cardtitle">
                        <h3>Schedules</h3>
                        <p>{schedules.length} active schedule{schedules.length !== 1 ? 's' : ''}</p>
                      </div>
                    </div>
                    <div style={{ padding: '20px' }}>
                      {schedules.length === 0 ? (
                        <p style={{ textAlign: 'center', color: '#666', padding: '20px' }}>
                          No schedules yet. Use ChatBot to create a schedule!
                        </p>
                      ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                          {schedules.map((schedule) => {
                            // Backend trả về giờ Việt Nam trực tiếp (format: "2026-01-03T20:59:00")
                            // Parse trực tiếp như LocalDateTime (giờ Việt Nam)
                            const scheduledTimeStr = schedule.scheduledTime;
                            // Parse như local time (giờ Việt Nam) - không thêm 'Z'
                            const scheduledTime = new Date(scheduledTimeStr);
                            const now = new Date();
                            
                            // Format time: HH:mm (24h format) - lấy trực tiếp từ giờ Việt Nam
                            const hours = scheduledTime.getHours().toString().padStart(2, '0');
                            const minutes = scheduledTime.getMinutes().toString().padStart(2, '0');
                            const timeStr = `${hours}:${minutes}`;
                            
                            const dateStr = scheduledTime.toLocaleDateString('vi-VN', {
                              day: '2-digit',
                              month: '2-digit',
                              year: scheduledTime.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
                            });
                            
                            return (
                              <div
                                key={schedule.id}
                                style={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  alignItems: 'center',
                                  padding: '12px',
                                  backgroundColor: '#f9fafb',
                                  borderRadius: '8px',
                                  border: '1px solid #e5e7eb'
                                }}
                              >
                                <div>
                                  <div style={{ fontWeight: '500', marginBottom: '4px' }}>
                                    {schedule.action === 'ON' ? '🟢 Turn On' : '🔴 Turn Off'} - {schedule.deviceName}
                                  </div>
                                  <div style={{ fontSize: '14px', color: '#666' }}>
                                    {dateStr} at {timeStr}
                                  </div>
                                </div>
                                <button
                                  onClick={() => handleDeleteSchedule(schedule.id)}
                                  style={{
                                    padding: '6px 12px',
                                    backgroundColor: '#ef4444',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    fontSize: '12px'
                                  }}
                                  onMouseEnter={(e) => e.target.style.backgroundColor = '#dc2626'}
                                  onMouseLeave={(e) => e.target.style.backgroundColor = '#ef4444'}
                                >
                                  Delete
                                </button>
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}

              {/* Scenes - Hiển thị cho tất cả thiết bị bao gồm Smart Socket */}
              <div className="dd-card">
                <div className="dd-scenesHead">
                  <div className="dd-cardtitle">
                    <h3>Scenes</h3>
                  </div>
                  <span className="dd-badge">{scenes.length}</span>
                </div>

                {scenes.map((s) => (
                  <div className="dd-sceneItem" key={s.id}>
                    <div className="dd-sceneText">
                      <b>{s.name}</b>
                      <span>{s.schedule}</span>
                    </div>
                    <label className="dd-toggle">
                      <input type="checkbox" checked={s.enabled} onChange={() => {}} />
                      <span className="dd-slider" />
                    </label>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default DeviceDetail;
